import React from "react"
import { Link } from "react-router-dom"

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link to="/users/sign_up" className="text-indigo-600 hover:text-indigo-500">
            &larr; Back to Sign Up
          </Link>
        </div>

        <div className="bg-white shadow rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: January 30, 2026</p>

          <div className="prose prose-indigo max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1. Introduction</h2>
              <p>
                Hosting Playground Inc ("Company", "we", "us", or "our") operates Heartily.app ("Service").
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                when you use our Service.
              </p>
              <p className="mt-3">
                Please read this Privacy Policy carefully. By using the Service, you consent to the collection
                and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2. Information We Collect</h2>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Account Information:</strong> Name, email address, password, and timezone</li>
                <li><strong>Canvas Integration:</strong> Canvas access tokens you provide to sync your assignments</li>
                <li><strong>Contact Information:</strong> Phone number (optional, for notifications)</li>
                <li><strong>Communication:</strong> Any information you provide when contacting us for support</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.2 Information from Canvas LMS</h3>
              <p>When you connect your Canvas account, we access:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Course information (names, terms, enrollment status)</li>
                <li>Assignment details (names, due dates, descriptions, points)</li>
                <li>Submission status and grades</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.3 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Usage Data:</strong> Pages visited, features used, and actions taken within the Service</li>
                <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
                <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Provide, maintain, and improve the Service</li>
                <li>Sync and display your Canvas assignments</li>
                <li>Send notifications about upcoming assignments and due dates</li>
                <li>Enable parent/guardian monitoring features (with your consent)</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Monitor and analyze usage patterns to improve user experience</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4. Information Sharing and Disclosure</h2>
              <p>We may share your information in the following circumstances:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>With Your Consent:</strong> When you explicitly authorize sharing, such as linking parent/guardian accounts</li>
                <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our Service (hosting, analytics, notifications)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
              <p className="mt-3">
                We do not sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Encryption of sensitive data (including Canvas access tokens) at rest and in transit</li>
                <li>Secure HTTPS connections for all communications</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls limiting who can view your data</li>
              </ul>
              <p className="mt-3">
                However, no method of transmission over the Internet is 100% secure, and we cannot guarantee
                absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6. Data Retention</h2>
              <p>
                We retain your information for as long as your account is active or as needed to provide you services.
                If you delete your account, we will delete or anonymize your personal information within 30 days,
                except where we are required to retain it for legal purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">7. Your Rights and Choices</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate personal information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and personal information</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, please contact us at privacy@heartily.app.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">8. Children's Privacy</h2>
              <p>
                Our Service is intended for students of all ages. For users under 13 years of age,
                we comply with the Children's Online Privacy Protection Act (COPPA). We require
                verifiable parental consent before collecting personal information from children under 13.
                Parents can review, delete, or refuse further collection of their child's information
                by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">9. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your own.
                These countries may have different data protection laws. By using the Service, you consent
                to the transfer of your information to the United States and other countries where we operate.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">10. Third-Party Links</h2>
              <p>
                The Service may contain links to third-party websites (such as Canvas LMS). We are not
                responsible for the privacy practices of these third parties. We encourage you to read
                their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">11. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes
                by posting the new Privacy Policy on this page and updating the "Last updated" date. We may also
                notify you via email for significant changes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">12. Contact Us</h2>
              <p>
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Hosting Playground Inc</strong><br />
                Email: privacy@heartily.app
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
